// src/api/messageFoldersApi.ts

import { baseApi } from './baseApi';
import type {
  SavedMessageFolder,
  SavedMessage,
  CreateFolderRequest,
  UpdateFolderRequest,
  SaveMessageToFolderRequest,
  MessageFolderInfo,
} from '../types/api';

interface ListFoldersParams {
  workspaceId?: string;
}

export const messageFoldersApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    // =============================================
    // Folders
    // =============================================
    getFolders: builder.query<SavedMessageFolder[], ListFoldersParams>({
      query: (params) => ({
        url: '/message-folders',
        params: {
          ...(params.workspaceId && { workspaceId: params.workspaceId }),
        },
      }),
      providesTags: (result, error, { workspaceId }) =>
        result
          ? [
              ...result.map(({ id }) => ({ type: 'MessageFolder' as const, id })),
              { type: 'MessageFolder', id: workspaceId || 'LIST' },
            ]
          : [{ type: 'MessageFolder', id: workspaceId || 'LIST' }],
    }),

    getFolder: builder.query<SavedMessageFolder, string>({
      query: (id) => `/message-folders/${id}`,
      providesTags: (result, error, id) => [{ type: 'MessageFolder', id }],
    }),

    createFolder: builder.mutation<SavedMessageFolder, CreateFolderRequest>({
      query: (body) => ({
        url: '/message-folders',
        method: 'POST',
        body,
      }),
      invalidatesTags: (result, error, { workspaceId }) => [
        { type: 'MessageFolder', id: workspaceId || 'LIST' },
      ],
    }),

    updateFolder: builder.mutation<SavedMessageFolder, { id: string } & UpdateFolderRequest>({
      query: ({ id, ...body }) => ({
        url: `/message-folders/${id}`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'MessageFolder', id },
      ],
    }),

    deleteFolder: builder.mutation<{ success: boolean }, string>({
      query: (id) => ({
        url: `/message-folders/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, id) => [
        { type: 'MessageFolder', id },
        { type: 'MessageFolder', id: 'LIST' },
      ],
    }),

    // =============================================
    // Saved Messages
    // =============================================
    saveMessageToFolder: builder.mutation<SavedMessage, { folderId: string } & SaveMessageToFolderRequest>({
      query: ({ folderId, ...body }) => ({
        url: `/message-folders/${folderId}/messages`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (result, error, { folderId }) => [
        { type: 'MessageFolder', id: folderId },
        { type: 'SavedMessage', id: 'LIST' },
      ],
    }),

    updateSavedMessage: builder.mutation<SavedMessage, { folderId: string; savedMessageId: string; note?: string }>({
      query: ({ folderId, savedMessageId, note }) => ({
        url: `/message-folders/${folderId}/messages/${savedMessageId}`,
        method: 'PATCH',
        body: { note },
      }),
      invalidatesTags: (result, error, { folderId, savedMessageId }) => [
        { type: 'MessageFolder', id: folderId },
        { type: 'SavedMessage', id: savedMessageId },
      ],
    }),

    removeMessageFromFolder: builder.mutation<{ success: boolean }, { folderId: string; savedMessageId: string }>({
      query: ({ folderId, savedMessageId }) => ({
        url: `/message-folders/${folderId}/messages/${savedMessageId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, { folderId }) => [
        { type: 'MessageFolder', id: folderId },
        { type: 'SavedMessage', id: 'LIST' },
      ],
    }),

    getMessageFolders: builder.query<MessageFolderInfo[], string>({
      query: (messageId) => `/messages/${messageId}/folders`,
      providesTags: (result, error, messageId) => [
        { type: 'SavedMessage', id: messageId },
      ],
    }),
  }),
});

export const {
  useGetFoldersQuery,
  useGetFolderQuery,
  useCreateFolderMutation,
  useUpdateFolderMutation,
  useDeleteFolderMutation,
  useSaveMessageToFolderMutation,
  useUpdateSavedMessageMutation,
  useRemoveMessageFromFolderMutation,
  useGetMessageFoldersQuery,
} = messageFoldersApi;
